import { InputNumber } from 'antd';
import { buildShippingCodLimitError, Permissions, type ShippingCodLimit } from '@tcr-erp/shared';
import { useRbac } from '@/hooks/useRbac';
import { formatNumber } from '@/utils/format';
import { clampShippingDebt } from '../../utils/shippingTotals';
import { MONEY_MAX, moneyFormatter, moneyParser, moneyInputKeyDownGuard } from '../../utils/moneyInput';

interface Props {
  /** `money.cod` (`PosMoneyResult`, `computePosMoney`) — COD đã trừ nợ. */
  cod: number;
  /** `money.debt` — số đã ghi nợ, đã kẹp `[0, codDefault]`. */
  debt: number;
  /** `money.codDefault` — COD TRƯỚC khi trừ nợ, làm trần cho ô nhập COD. */
  codDefault: number;
  /** `money.orderRevenue`. */
  orderRevenue: number;
  /** Hạn mức COD của hãng đang chọn. Bỏ trống = không chặn. */
  codLimit?: ShippingCodLimit;
  /** Báo lên số NỢ mới (đã kẹp). Vắng mặt ⇒ khối hiển thị read-only. */
  onDebtChange?: (debtAmount: number) => void;
  /**
   * Bật kiểu input gọn (underlined, size small, căn phải) cho ô "Phải thu COD"
   * ở chế độ sửa được. Component này dùng chung giữa POS (cột phải, cần gọn)
   * và Edit Order (`src/pages/Orders/edit.tsx`, PHẢI giữ nguyên giao diện cũ) —
   * mặc định `false` để Edit Order không bị ảnh hưởng khi không truyền prop
   * này; chỉ POS truyền `compact` để opt-in kiểu mới.
   */
  compact?: boolean;
}

/**
 * Hai con số chốt của đơn giao hàng, đặt ở CUỐI cụm tiền cột phải — sau khi thu
 * ngân đã nhập xong phí ship, trả trước và chọn hình thức thanh toán.
 *
 * Tách khỏi khối nhập liệu (`OrderInfoPanel`) vì giữa hai phần còn có
 * "Hình thức thanh toán" chen vào; một component không render vào hai chỗ được.
 * Task 18: KHÔNG tự tính gì nữa — `cod`/`debt`/`codDefault`/`orderRevenue` đều
 * là kết quả `computePosMoney` (cùng hàm BE dùng) nhận thẳng từ ngoài.
 */
export function ShippingTotals({
  cod,
  debt,
  codDefault,
  orderRevenue,
  codLimit,
  onDebtChange,
  compact = false,
}: Props): JSX.Element {
  const { hasPermission } = useRbac();
  const codEffective = cod;
  const editable = !!onDebtChange && hasPermission(Permissions.POS_COD_ADJUST);

  // COD ở POS là số TÍNH RA (phải thu = tiền hàng − trả trước + phí), không phải
  // ô người dùng gõ. Nên cảnh báo phải nói cả cách xử lý, không chỉ nói "vượt".
  // Dùng số ĐÃ TRỪ NỢ: đó mới là số thật báo cho hãng vận chuyển thu hộ.
  const codError = buildShippingCodLimitError(codEffective, codLimit);

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <tbody>
        <tr>
          <td style={{ color: '#a4193d', fontWeight: 700, fontSize: 15, paddingBottom: 8 }}>
            Phải thu COD:
          </td>
          {editable ? (
            <td style={{ paddingBottom: 8 }}>
              <InputNumber
                aria-label="Phải thu COD"
                {...(compact
                  ? {
                      className: 'pos-money-input-right',
                      variant: 'underlined' as const,
                      size: 'small' as const,
                      // Stepper nổi đè lên chữ số căn phải khi hover/focus. Chỉ
                      // tắt ở POS — Edit Order giữ nguyên giao diện cũ.
                      controls: false,
                    }
                  : {})}
                style={{ width: '100%' }}
                min={0}
                max={Math.min(MONEY_MAX, codDefault)}
                value={codEffective}
                formatter={moneyFormatter}
                parser={moneyParser}
                onKeyDown={moneyInputKeyDownGuard}
                onChange={(v) => {
                  const next = Math.min(Math.max(0, v ?? 0), codDefault);
                  // `clampShippingDebt` kẹp lại debt cho CHẮC (ceiling `codDefault`
                  // âm là trường hợp lý thuyết, nhưng cùng một hàm cho mọi nơi thu
                  // ngân gõ nợ là bất biến task này giữ, không tự trừ tay ở đây).
                  onDebtChange?.(clampShippingDebt(codDefault - next, codDefault).debt);
                }}
              />
            </td>
          ) : (
            <td
              data-testid="cod-amount"
              style={{
                textAlign: 'right',
                fontWeight: 700,
                fontSize: 15,
                color: '#a4193d',
                paddingBottom: 8,
              }}
            >
              {formatNumber(codEffective)}
            </td>
          )}
        </tr>
        {debt > 0 && (
          <tr>
            <td style={{ color: '#d46b08', fontWeight: 700, paddingBottom: 8 }}>Công nợ:</td>
            <td
              data-testid="debt-amount"
              style={{ textAlign: 'right', fontWeight: 700, color: '#d46b08', paddingBottom: 8 }}
            >
              {formatNumber(debt)}
            </td>
          </tr>
        )}
        {codError && (
          <tr>
            <td colSpan={2} style={{ paddingBottom: 8 }}>
              <div
                role="alert"
                data-testid="cod-limit-warning"
                style={{
                  color: '#ff4d4f',
                  fontSize: 12,
                  lineHeight: 1.5,
                  background: '#fff2f0',
                  border: '1px solid #ffccc7',
                  borderRadius: 4,
                  padding: '6px 8px',
                }}
              >
                {codError}
              </div>
            </td>
          </tr>
        )}
        <tr>
          <td style={{ color: '#333', fontWeight: 700, fontSize: 15 }}>Doanh thu đơn hàng:</td>
          <td
            data-testid="order-revenue"
            style={{ textAlign: 'right', fontWeight: 700, fontSize: 15 }}
          >
            {formatNumber(orderRevenue)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
